import { format } from 'date-fns';
import { z } from 'zod';

import { tryReadDate } from '@elara/core/time-utils';
import { pathId } from '@elara/firebolt/TemplatePath';

/**
 * A generic, reusable `pathId` placeholder for use in `TemplatePath` definitions.
 * This represents a generic document ID in a Firestore path and is a key part of the generic
 * ID refactoring, allowing for the creation of reusable model specifications.
 *
 * @example
 * // Used in a ModelSpec path definition:
 * const UserProfileSpec = defineModelSpec({
 *   path: collectionPath`users/${id}`,
 *   // ... shape
 * });
 */
export const id = pathId('id');
/** Collection path placeholder for chatIds. */
export const chatId = pathId('chatId');
/** Collection path placeholder for messageIds. */
export const chatMessageId = pathId('messageId');
/** Collection path placeholder for userIds. */
export const userId = pathId('userId');
/** Collection path placeholder for tripIds. */
export const tripId = pathId('tripId');
/** Collection path placeholder for hotelIds. */
export const hotelId = pathId('hotelId');
/** Collection path placeholder for waitlist submissionIds. */
export const submissionId = pathId('submissionId');

function dateToSabreFormat(date: string | Date): string {
  const [value] = tryReadDate(date);
  return format(value, 'yyyy-MM-dd');
}

export function normalizeSabreDate(date: string | Date): string {
  return dateToSabreFormat(date);
}

export const zodSabreDate = (description: string) =>
  z
    .string()
    .check(({ value }) => {
      z.iso.date().parse(value);
    })
    .describe(`${description} as YYYY-MM-DD`);

export const ZodDepartureDate = zodSabreDate('Departure date');
export const ZodReturnDate = zodSabreDate('Return date');
export const ZodAdultsCount = z.number().default(1).describe('Adults');
export const ZodChildrenCount = z.number().default(0).describe('Children under 18');
export const ZodInfantCount = z.number().default(0).describe('Infants under 2');
export const ZodSabreCabinClass = z.enum([
  'PremiumFirst',
  'First',
  'PremiumBusiness',
  'Business',
  'PremiumEconomy',
  'Economy',
]);
export const ZodGoogleCabinClass = z.enum(['Economy', 'Premium economy', 'Business', 'First']);
export const zodAirportCode = (description: string) => z.string().describe(`${description} airport IATA`);
export const ZodOriginAirport = zodAirportCode('Origin');
export const ZodDestinationAirport = zodAirportCode('Destination');
export const ZodAge = z.number();
